import type { Vec2, Vec3 } from '../../../geo/types';
import type { MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { three as painterCamera } from '../../../data/painterView.json';
import committedBaseLines from './painterBaseLines.json';

/**
 * The painter stood every part of the big buildings on its near base line and turned them
 * broadside (docs/plans/landmarks.md 6.4): their far parts stand on the canvas where their near
 * foot is, so the model, standing each part at its surveyed depth, draws them up to 50 frame px too
 * high. In the painter view only (aPainterOffset), each vertex moves level toward the painter along
 * his forward direction until it keeps a twentieth of its depth past the painted base line: enough
 * to keep the parts in their order in the depth buffer (near plane 20 m, about 3 cm of depth at
 * 3.4 km), too little to move a row by a pixel.
 */
export const DEPTH_KEPT = 0.05;

function groundOfCamera(): { readonly eye: Vec2; readonly forward: Vec2 } {
  const [px, , pz] = painterCamera.position;
  const [tx, , tz] = painterCamera.target;
  const length = Math.hypot(tx! - px!, tz! - pz!);
  return { eye: [px!, pz!], forward: [(tx! - px!) / length, (tz! - pz!) / length] };
}

/** The painter's eye on the ground and his forward direction, in world x and z. */
export const PAINTER_GROUND = groundOfCamera();

/**
 * The depth from the painter's eye of each building's painted base line, in metres: fitted to the
 * painted rows of its bases, eaves, ridges and tops (landmarks.md 6.4, and the town hall's foot at
 * frame row 563 with its heights over it, section 3) under the committed warp. Generated by
 * node tools/painterBaseLines/run.mjs; rerun it when the painted rows, the painter mapping or the
 * warp change (silhouette/baseLineFit.test.ts fails while the file is stale).
 */
export const PAINTED_BASE_LINES: Readonly<Record<string, number>> = committedBaseLines;

/**
 * Buildings whose painted foot the houses in front of them hide (cut row 864 over a base at 870,
 * landmarks.md 6.1), so no painted row places their near parts. The painter drew those houses up to
 * the church; moving its near parts back onto the line would open the churchyard, which he did not
 * paint, where his roofs stand (district (3,3), landmarks.md 6.5). Their near parts keep their depth.
 */
export const FOOT_HIDDEN: ReadonlySet<string> = new Set(['landmark-oude-kerk']);

/** The offset that stands a vertex on the base line at `depth`, a twentieth of its own depth kept. */
export function painterBaseLineOffset(depth: number, keepNear = false): (world: Vec3) => Vec3 {
  const { eye, forward } = PAINTER_GROUND;
  return (world) => {
    const past = (world[0] - eye[0]) * forward[0] + (world[2] - eye[1]) * forward[1] - depth;
    const toward = (keepNear ? Math.max(0, past) : past) * (1 - DEPTH_KEPT);
    return [-forward[0] * toward, 0, -forward[1] * toward];
  };
}

/** A building's base line offset, or undefined for one the painter did not stand on a base line. */
export function baseLineOffsetOf(landmarkId: string): ((world: Vec3) => Vec3) | undefined {
  const depth = PAINTED_BASE_LINES[landmarkId];
  return depth === undefined
    ? undefined
    : painterBaseLineOffset(depth, FOOT_HIDDEN.has(landmarkId));
}

function placedStyle(style: VertexStyle, painterOffset: (world: Vec3) => Vec3): VertexStyle {
  return { ...style, painterOffset };
}

/** The builder a landmark is built through: the same builder, its styles carrying the offset. */
export function painterPlaced(builder: MeshBuilder, landmarkId: string): MeshBuilder {
  const offsetOf = baseLineOffsetOf(landmarkId);
  if (offsetOf === undefined) return builder;
  const at = (style: VertexStyle) => placedStyle(style, offsetOf);
  return {
    quad: (style, ...rest) => {
      builder.quad(at(style), ...rest);
    },
    polygon: (style, ...rest) => {
      builder.polygon(at(style), ...rest);
    },
    box: (style, ...rest) => {
      builder.box(at(style), ...rest);
    },
    prism: (style, ...rest) => {
      builder.prism(at(style), ...rest);
    },
    cylinder: (style, ...rest) => {
      builder.cylinder(at(style), ...rest);
    },
    groundPolygon: (style, ...rest) => {
      builder.groundPolygon(at(style), ...rest);
    },
    get vertexCount() {
      return builder.vertexCount;
    },
    get triangleCount() {
      return builder.triangleCount;
    },
    build: () => builder.build(),
    finish: () => builder.finish(),
  };
}
